import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertFromRaw } from 'draft-js';
import { format } from 'date-fns';
import { getCompanyActivity } from '../../services/company_services';
import { ReactComponent as SpeakerIcon } from '../../icons/speaker.svg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { buildEmbeddableUrl } from '../../utils/video';

const FETCH_LIMIT = 10;
const PREVIEW_CHAR_LIMIT = 220;
const CUT_OFF_DATE = new Date('2025-11-01T00:00:00Z');

export default function LatestBoardUpdate({ companyId }) {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const [activity, setActivity] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [editorState, setEditorState] = useState(null);

  useEffect(() => {
    if (!companyId) return undefined;
    let isMounted = true;
    setActivity(null);
    setExpanded(false);

    getCompanyActivity({
      companyId,
      limit: FETCH_LIMIT,
      offset: 0,
      filterType: ['postUpdateCreated'],
    })
      .then((response) => {
        if (!isMounted) return;
        const activities = response?.data?.getCompanyActivity ?? [];
        const validUpdates = activities
          .filter((item) => {
            if (!item?.postUpdate) return false;
            const createdDate =
              item.postUpdate?.createdAt || item.createdAt || null;
            if (!createdDate) return false;
            return new Date(createdDate) > CUT_OFF_DATE;
          })
          .sort((a, b) => {
            const dateA =
              new Date(a.postUpdate?.createdAt || a.createdAt).getTime() || 0;
            const dateB =
              new Date(b.postUpdate?.createdAt || b.createdAt).getTime() || 0;
            return dateB - dateA;
          });
        if (validUpdates.length > 0) {
          setActivity(validUpdates[0]);
        }
      })
      .catch((error) => {
        console.error('Error fetching latest board update', error);
      });

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!activity?.postUpdate?.body) {
      setEditorState(null);
      return;
    }

    try {
      const contentState = convertFromRaw(JSON.parse(activity.postUpdate.body));
      setEditorState(EditorState.createWithContent(contentState));
    } catch (error) {
      setEditorState(null);
    }
  }, [activity?.postUpdate?.body]);

  const postUpdate = activity?.postUpdate;

  const createdDate = postUpdate?.createdAt || activity?.createdAt || null;
  const formattedDate = createdDate
    ? format(new Date(createdDate), 'MMM. d, yyyy')
    : '';

  const plainBody = useMemo(() => {
    if (!postUpdate?.body) return '';
    try {
      const parsed = convertFromRaw(JSON.parse(postUpdate.body));
      return parsed.getPlainText('\n');
    } catch (error) {
      return postUpdate?.body || '';
    }
  }, [postUpdate?.body]);

  const truncatedBody =
    plainBody.length > PREVIEW_CHAR_LIMIT
      ? `${plainBody.slice(0, PREVIEW_CHAR_LIMIT).trim()}…`
      : plainBody;

  const hasMoreContent = plainBody.length > PREVIEW_CHAR_LIMIT;

  const videoUrl = useMemo(
    () => buildEmbeddableUrl(postUpdate?.video?.url || postUpdate?.streamLink),
    [postUpdate?.video?.url, postUpdate?.streamLink]
  );
  const hasVideo = Boolean(videoUrl);
  const canExpand = hasVideo || hasMoreContent;
  const watchVideoButton = hasVideo ? (
    <Button
      onClick={() => setExpanded(true)}
      variant="text"
      startIcon={
        <PlayCircleFilledWhiteIcon
          sx={{ color: '#2B71F6', fontSize: '22px' }}
        />
      }
      sx={{
        textTransform: 'none',
        fontWeight: 400,
        fontSize: 16,
        boxShadow: 'none',
        p: 0,
        color: '#001030',
        minWidth: 'fit-content',
        alignSelf: isSmallScreen ? 'flex-start' : 'auto',
      }}
    >
      Watch Video
    </Button>
  ) : null;
  const renderFullBody = () => {
    if (!postUpdate?.body) {
      return null;
    }

    if (editorState && editorState.getCurrentContent().hasText()) {
      return (
        <Editor
          toolbarHidden
          readOnly
          editorState={editorState}
          editorStyle={{
            color: '#4D586F',
            fontSize: '14px',
            lineHeight: '22px',
            marginTop: '-10px',
            marginBottom: '-10px',
          }}
        />
      );
    }

    return (
      <Typography
        sx={{
          whiteSpace: 'pre-wrap',
          mt: 1,
          color: '#4D586F',
          fontSize: 14,
          lineHeight: '150%',
          fontWeight: 400,
        }}
      >
        {postUpdate.body}
      </Typography>
    );
  };
  const bodyContent = !expanded ? (
    <Typography
      sx={{
        mt: 1,
        color: '#4D586F',
        fontSize: 14,
        lineHeight: '150%',
        fontWeight: 400,
        display: 'inline',
      }}
    >
      {truncatedBody}
      {canExpand && (
        <Button
          onClick={() => setExpanded(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            color: '#0A5CFF',
            p: 0,
            minWidth: 'fit-content',
            ml: 1,
          }}
        >
          Read More
        </Button>
      )}
    </Typography>
  ) : (
    <Box>
      {renderFullBody()}
      {hasVideo && videoUrl ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 500,
            height: 300,
            mt: 3,
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          <Box
            key={videoUrl}
            component="iframe"
            src={videoUrl}
            title="Board update video"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        </Box>
      ) : null}
      {canExpand && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 1,
          }}
        >
          <Button
            onClick={() => setExpanded(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              color: '#0A5CFF',
              p: 0,
              minWidth: 'fit-content',
            }}
          >
            Show Less
          </Button>
        </Box>
      )}
    </Box>
  );
  const iconElement = (
    <Box
      sx={{
        width: 60,
        height: 60,
        borderRadius: '9px',
        backgroundColor: '#97D3FC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <SpeakerIcon width={32} height={32} />
    </Box>
  );

  if (!postUpdate) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        mb: 0,
        borderRadius: '12px',
        backgroundColor: '#FFFFFF',
        p: { xxs: 2, sm: 3 },
      }}
    >
      {isSmallScreen ? (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {iconElement}
            <Box sx={{ flex: 1 }}>
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#001030',
                  }}
                >
                  Latest Board Update
                </Typography>
                {(formattedDate || watchVideoButton) && (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    // justifyContent="space-between"
                    sx={{ flexWrap: 'wrap', rowGap: 0.5, width: '100%' }}
                  >
                    {formattedDate && (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: 16,
                          color: '#001030',
                        }}
                      >
                        {formattedDate}
                      </Typography>
                    )}
                    {watchVideoButton}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
          {bodyContent}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
          }}
        >
          {iconElement}
          <Box sx={{ flex: 1 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              // justifyContent="space-between"
              sx={{ flexWrap: 'wrap' }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#001030',
                  }}
                >
                  Latest Board Update
                </Typography>
                {formattedDate && (
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: 16,
                      color: '#001030',
                    }}
                  >
                    {formattedDate}
                  </Typography>
                )}
              </Stack>
              {watchVideoButton}
            </Stack>
            {bodyContent}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

LatestBoardUpdate.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
